import React, { useState, useEffect } from 'react';
import { useAuth } from '@/stores/auth';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  getStaff, getStaffById, postStaff, putStaff,
  getClasses, getSubjects, getTeacherAssignments, postTeacherAssignment, deleteTeacherAssignment,
  getOwnerWalletBanks, postOwnerWalletResolveAccount,
  putPayrollSalary, getPayrollSalaries, getPayrollDeductions, postPayrollDeduction, deletePayrollDeduction,
  getPayrollHistory
} from '@/api/owner';
import { Plus, Edit2, Users, CheckCircle2, AlertCircle, Trash2, BookOpen, DollarSign, MinusCircle, KeyRound, X } from 'lucide-react';

const arr = <T,>(s: any): T[] => {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  if (Array.isArray(s.data)) return s.data;
  if (Array.isArray(s.data?.data)) return s.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;

export function StaffPage() {
  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pwFor, setPwFor] = useState<any | null>(null);

  const staffQ = useQuery({ queryKey: ['owner-staff'], queryFn: () => getStaff().then(unwrap) });
  const staff = arr<any>(staffQ.data);

  const filtered = staff.filter((s: any) => {
    if (roleFilter !== 'all' && s.role !== roleFilter) return false;
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const hay = `${s.name ?? ''} ${s.phone ?? ''} ${s.email ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  if (view === 'create') return <StaffForm onBack={() => { setView('list'); staffQ.refetch(); }} />;
  if (view === 'edit' && editId) return <StaffForm staffId={editId} onBack={() => { setView('list'); staffQ.refetch(); }} />;

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">PEOPLE & ACCESS</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Staff</h1>
          <p className="text-slate-500 text-sm mt-1">Add staff with a role and password · they sign in with their phone or email</p>
        </div>
        <button onClick={() => setView('create')} className="px-5 py-2.5 text-sm font-bold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-2 shadow-sm transition-colors">
          <Plus className="w-4 h-4" /> Add staff
        </button>
      </div>

      <div className="bg-white p-3 rounded-xl border border-slate-200 flex flex-wrap gap-3 items-center shadow-sm">
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium">
          <option value="all">All roles</option>
          <option value="teacher">Teachers</option><option value="desk">Desk Officers</option><option value="bursar">Bursars</option><option value="owner">Owners</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium">
          <option value="all">All statuses</option><option value="active">Active</option><option value="invited">Invited</option><option value="inactive">On leave</option>
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, or email" className="flex-1 min-w-[200px] border border-slate-300 rounded-lg px-3 py-2 text-sm" />
      </div>

      {staffQ.isLoading ? (
        <p className="text-sm text-slate-500 text-center py-8">Loading staff…</p>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center bg-white border border-slate-200 rounded-xl shadow-sm">
          <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-700">{staff.length === 0 ? 'No staff yet' : 'No staff match these filters'}</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-4 py-4 text-left">Name</th>
                <th className="px-4 py-4 text-left">Role</th>
                <th className="px-4 py-4 text-left">Status</th>
                <th className="px-4 py-4 text-left">Last active</th>
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s: any) => (
                <tr key={s.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-4 py-4"><p className="font-bold text-slate-900">{s.name}</p></td>
                  <td className="px-4 py-4 text-slate-700 text-xs font-bold"><span className="capitalize">{s.role === 'desk' ? 'Desk officer' : s.role}</span>{s.custom_role && <span className="block text-[11px] font-semibold text-blue-700">{s.custom_role}</span>}</td>
                  <td className="px-4 py-4">
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border ${
                      s.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' : 
                      s.status === 'invited' ? 'bg-amber-100 text-amber-800 border-amber-200' : 
                      'bg-slate-100 text-slate-600 border-slate-200'
                    }`}>{s.status === 'inactive' ? 'On leave' : s.status}</span>
                  </td>
                  <td className="px-4 py-4 text-xs text-slate-500">{s.last_login_at ? new Date(s.last_login_at).toLocaleDateString() : 'Never'}</td>
                  <td className="px-4 py-4 text-right">
                    <span className="inline-flex items-center gap-3">
                      {s.role !== 'owner' && <button onClick={() => setPwFor(s)} className="text-xs font-bold text-slate-700 hover:text-blue-600 hover:underline inline-flex items-center gap-1">
                        <KeyRound className="w-3.5 h-3.5" /> Password
                      </button>}
                      <button onClick={() => { setEditId(s.id); setView('edit'); }} className="text-xs font-bold text-slate-700 hover:text-blue-600 hover:underline inline-flex items-center gap-1">
                        <Edit2 className="w-3.5 h-3.5" /> Edit
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pwFor && <StaffPasswordModal staff={pwFor} onClose={() => setPwFor(null)} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   STAFF FORM (Full Page)
   ═══════════════════════════════════════════════════════════════ */
function StaffForm({ staffId, onBack }: { staffId?: number; onBack: () => void }) {
  const isCreate = !staffId;
  const [tab, setTab] = useState<'profile' | 'bank' | 'teaching' | 'payroll' | 'history'>('profile');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const staffQ = useQuery({
    queryKey: ['owner-staff-detail', staffId],
    queryFn: () => getStaffById(staffId!).then(unwrap),
    enabled: !isCreate,
  });

  const [localStaff, setLocalStaff] = useState<any>(staffQ.data);
  useEffect(() => { if (staffQ.data) setLocalStaff(staffQ.data); }, [staffQ.data]);

  const isTeacher = localStaff?.role === 'teacher';

  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl flex gap-2 items-center shadow-sm"><AlertCircle className="w-5 h-5" /> {error}</div>}
      {successMsg && <div className="bg-green-50 border border-green-200 text-green-700 p-4 rounded-xl flex gap-2 items-center shadow-sm"><CheckCircle2 className="w-5 h-5" /> {successMsg}</div>}

      <div className="flex justify-between items-center">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">PEOPLE & ACCESS</p>
          <h1 className="text-3xl font-extrabold text-slate-900">{isCreate ? 'Add staff' : 'Staff Details'}</h1>
          <p className="text-sm text-slate-500">{isCreate ? 'Invite goes by SMS' : localStaff?.name ?? 'Loading...'}</p>
        </div>
        <button onClick={onBack} className="px-5 py-2.5 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 shadow-sm transition-colors">Cancel</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex border-b border-slate-200 mb-6 gap-2 overflow-x-auto">
            <button onClick={() => setTab('profile')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'profile' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Profile</button>
            <button onClick={() => setTab('bank')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'bank' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Bank</button>
            {isTeacher && (
              <button onClick={() => setTab('teaching')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'teaching' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Teaching</button>
            )}
            <button onClick={() => setTab('payroll')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'payroll' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Payroll Setup</button>
            <button onClick={() => setTab('history')} className={`pb-3 px-4 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${tab === 'history' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>Payment History</button>
          </div>

          <div className="pt-2">
            {tab === 'profile' && (
              <ProfileForm 
                staff={localStaff} 
                onSave={(newStaff: any, msg: string) => { setLocalStaff(newStaff); setSuccessMsg(msg); if (!isCreate) onBack(); }} 
                onError={(m: string) => setError(m)} 
              />
            )}
            {tab === 'bank' && localStaff && (
              <BankForm 
                staff={localStaff} 
                onSave={(savedBank: any) => { setLocalStaff({ ...localStaff, profile: { ...localStaff.profile, ...savedBank } }); setSuccessMsg('Bank saved!'); }} 
                onError={(m: string) => setError(m)} 
              />
            )}
            {tab === 'teaching' && localStaff && isTeacher && (
              <TeachingForm staff={localStaff} onError={(m: string) => setError(m)} />
            )}
            {tab === 'payroll' && localStaff && (
              <PayrollTab staff={localStaff} onSave={(msg: string) => { setSuccessMsg(msg); }} onError={(m: string) => setError(m)} />
            )}
            {tab === 'history' && localStaff && (
              <PayrollHistoryTab staff={localStaff} />
            )}
          </div>
        </div>

        <div className="md:col-span-1 bg-slate-50/50 rounded-xl border border-slate-200 p-6 h-fit shadow-sm">
          <h3 className="font-extrabold text-slate-900 mb-6 text-lg">Result</h3>
          <div className="space-y-4 text-sm">
            <div className="flex justify-between border-b border-slate-200 pb-3">
              <span className="text-slate-500 font-semibold">Login</span>
              <span className="font-bold text-slate-900">{localStaff?.id ? 'Active' : 'Created on save'}</span>
            </div>
            <div className="flex justify-between border-b border-slate-200 pb-3">
              <span className="text-slate-500 font-semibold">Bank</span>
              <span className={`font-bold ${localStaff?.profile?.account_number || localStaff?.bank_details?.account_number ? 'text-green-700' : 'text-amber-700'}`}>
                {localStaff?.profile?.account_number || localStaff?.bank_details?.account_number ? 'Added' : 'Pending'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProfileForm({ staff, onSave, onError }: any) {
  const isDesk = useAuth((x) => x.user?.role) === 'desk';
  const [form, setForm] = useState({
    first_name: staff?.first_name ?? '', last_name: staff?.last_name ?? '',
    role: staff?.role ?? 'teacher', status: staff?.status ?? 'active',
    phone: staff?.phone ?? '', email: staff?.email ?? '', password: '',
  });
  const [saving, setSaving] = useState(false);
  // the dropdown value: a built-in role ("teacher") or a custom role ("c:12")
  const [pick, setPick] = useState<string>(staff?.custom_role_id ? `c:${staff.custom_role_id}` : (staff?.role ?? 'teacher'));
  const [mustChange, setMustChange] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const rolesQ = useQuery({ queryKey: ['school-custom-roles'], enabled: !isDesk, queryFn: () => api.get('/school/roles').then((r) => r.data?.data) });
  const customs: any[] = (rolesQ.data?.roles ?? []).filter((r: any) => r.base_role);
  const baseLabel: Record<string, string> = { teacher: 'Teacher', desk: 'Desk officer', bursar: 'Bursar' };
  const chosenCustom = pick.startsWith('c:') ? customs.find((r) => `c:${r.id}` === pick) : null;

  useEffect(() => {
    if (staff) {
      setPick(staff.custom_role_id ? `c:${staff.custom_role_id}` : (staff.role ?? 'teacher'));
      setForm({
        first_name: staff.first_name ?? staff.name?.split(' ')[0] ?? '',
        last_name: staff.last_name ?? staff.name?.split(' ').slice(1).join(' ') ?? '',
        role: staff.role ?? 'teacher',
        status: staff.status ?? 'active',
        phone: staff.phone ?? '',
        email: staff.email ?? '',
        password: '',
      });
    }
  }, [staff]);

  const save = async () => {
    setSaving(true);
    try {
      const payload: any = { ...form };
      if (chosenCustom) { payload.role = chosenCustom.base_role; payload.custom_role_id = chosenCustom.id; }
      else { payload.role = pick; if (!isDesk && (staff?.id ? staff.custom_role_id : false)) payload.custom_role_id = null; }
      if (staff?.id || !payload.password) delete payload.password;
      if (!staff?.id) payload.must_change_password = mustChange;
      if (isDesk) delete payload.custom_role_id;
      if (staff?.id) {
        await putStaff(staff.id, payload);
        onSave({ ...staff, ...payload, name: `${form.first_name} ${form.last_name}` }, 'Profile updated.');
      } else {
        delete (payload as any).status; // Avoid 422 on create
        const res = await postStaff(payload);
        const d = unwrap(res);
        const newId = d?.id ?? d?.user?.id;
        if (newId && form.status === 'active' && !isDesk) await putStaff(newId, { status: 'active' });
        onSave({ ...payload, id: newId, status: form.status, name: `${form.first_name} ${form.last_name}` }, d?.temp_password ? `Staff added. They sign in with ${form.phone || form.email} and password ${d.temp_password}${form.phone ? ' (also sent by SMS)' : ''}${mustChange ? ', then choose their own' : ''}. Now add their bank and salary.` : 'Staff added.');
      }
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">First Name *</label><input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Last Name *</label><input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Email</label><input value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        <div className="sm:col-span-2"><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Role</label>
          <select value={pick} onChange={e => setPick(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
            <optgroup label="Built-in roles">
              <option value="teacher">Teacher</option>{!isDesk && <><option value="desk">Desk officer</option><option value="bursar">Bursar</option></>}
            </optgroup>
            {!isDesk && customs.length > 0 && <optgroup label="Custom roles">{customs.map((r) => <option key={r.id} value={`c:${r.id}`}>{r.name} ({baseLabel[r.base_role] ?? r.base_role})</option>)}</optgroup>}
          </select>
          {chosenCustom ? <p className="text-[11px] text-slate-500 mt-1">Works as a {baseLabel[chosenCustom.base_role]?.toLowerCase()} with the pages set for “{chosenCustom.name}” ({(chosenCustom.pages_total ?? 0) - (chosenCustom.hidden_pages?.length ?? 0)} of {chosenCustom.pages_total} on).</p>
            : isDesk ? <p className="text-[11px] text-slate-500 mt-1">Desk officers add and edit teachers. Other roles are added by the owner.</p>
            : <p className="text-[11px] text-slate-500 mt-1">New roles you create under Roles & permissions show up here.</p>}
        </div>
        <div className={isDesk && staff?.id ? 'hidden' : ''}><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Status</label>
          <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
            <option value="active">Active</option><option value="inactive">On leave</option>
          </select>
        </div>
      </div>
      {staff?.id ? (
        <p className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg p-2.5">Password: use <b>Reset password</b> on the staff list. They sign in with their phone or email and the school code.</p>
      ) : (
        <div className="rounded-xl border border-slate-200 p-3 space-y-2">
          <label className="text-xs font-bold text-slate-500 uppercase block">Password <span className="normal-case font-normal">(optional)</span></label>
          <div className="relative"><input type={showPw ? 'text' : 'password'} value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Leave empty and we'll make one" autoComplete="new-password" className="w-full border border-slate-300 rounded-lg p-2.5 pr-16 text-sm" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500">{showPw ? 'Hide' : 'Show'}</button></div>
          {form.password && form.password.length < 6 && <p className="text-[11px] text-red-600">At least 6 characters.</p>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mustChange} onChange={e => setMustChange(e.target.checked)} className="w-4 h-4 accent-[#0A2540]" />Ask them to choose their own password after first sign-in</label>
          <p className="text-[11px] text-slate-500">They sign in with their phone or email and this password. It is shown to you once after saving{form.phone ? ' and texted to them' : ''}.</p>
        </div>
      )}
      <button onClick={save} disabled={saving || (!staff?.id && !!form.password && form.password.length < 6)} className="w-full mt-6 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800 disabled:opacity-50">
        {saving ? 'Saving…' : 'Save Profile'}
      </button>
    </div>
  );
}

function BankForm({ staff, onSave, onError }: any) {
  const qc = useQueryClient();
  const banksQ = useQuery({ queryKey: ['owner-wallet-banks'], queryFn: () => getOwnerWalletBanks().then(unwrap) });
  const banks = arr<any>(banksQ.data);

  const salariesQ = useQuery({ queryKey: ['payroll-salaries', staff.id], queryFn: () => getPayrollSalaries({ user_id: staff.id }).then(unwrap) });
  const salaryData = arr<any>(salariesQ.data).find((s: any) => s.user_id === staff.id);

  // Use the bank string from payroll data if available
  const hasBank = salaryData?.has_bank_account || staff.profile?.account_number;
  const bankString = salaryData?.bank || (staff.profile?.account_number ? `${staff.profile.bank_name || 'Bank'} ••••${String(staff.profile.account_number).slice(-4)}` : '');

  const [bankCode, setBankCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');

  const verify = async (acct: string) => {
    if (acct.length === 10 && bankCode) {
      try {
        const r: any = await postOwnerWalletResolveAccount({ bank_code: bankCode, account_number: acct });
        setAccountName(r.data?.data?.account_name ?? r.data?.account_name);
      } catch (e) { onError('Verification failed'); }
    }
  };

  const save = async () => {
    if (!bankCode || !accountNumber || !accountName) return onError('Fill all bank fields');
    try {
      const bankName = banks.find((b: any) => b.code === bankCode)?.name;
      await putStaff(staff.id, { bank_code: bankCode, bank_name: bankName, account_number: accountNumber, account_name: accountName });
      
      // Force refetch of salaries and staff profile
      salariesQ.refetch();
      qc.invalidateQueries({ queryKey: ['owner-staff-detail', staff.id] });
      
      onSave({ bank_code: bankCode, account_number: accountNumber, account_name: accountName, bank_name: bankName });
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); }
  };

  if (hasBank) {
    return (
      <div className="space-y-5">
        <div className="p-5 border border-green-200 bg-green-50 rounded-xl flex items-center gap-4 shadow-sm">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-green-200 text-green-600">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-slate-900">{bankString}</p>
            <p className="text-xs text-green-600 font-semibold mt-0.5">Verified Account</p>
          </div>
          <button onClick={() => { /* Reset to show form */ onSave(null); }} className="text-sm font-bold text-blue-600 hover:underline">Change</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Bank</label>
        <select value={bankCode} onChange={e => { setBankCode(e.target.value); setAccountName(''); }} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
          <option value="">Select Bank</option>{banks.map((b: any) => <option key={b.code} value={b.code}>{b.name}</option>)}
        </select>
      </div>
      <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Account Number</label>
        <input value={accountNumber} onChange={e => { setAccountNumber(e.target.value.replace(/\D/g, '')); if (e.target.value.length === 10) verify(e.target.value); }} maxLength={10} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm font-mono" />
      </div>
      <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Account Name</label>
        <input value={accountName} readOnly placeholder="Auto-filled" className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-slate-50" />
      </div>
      <button onClick={save} className="w-full mt-6 py-3 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800">Save Bank</button>
    </div>
  );
}

function TeachingForm({ staff, onError }: { staff: any; onError: (m: string) => void }) {
  const [classId, setClassId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  
  const classesQ = useQuery({ queryKey: ['owner-classes'], queryFn: () => getClasses().then(unwrap) });
  const subjectsQ = useQuery({ queryKey: ['owner-subjects'], queryFn: () => getSubjects().then(unwrap) });
  
  // Fetch from teacher-assignments to get the ID for unassigning
  const assignmentsQ = useQuery({
    queryKey: ['teacher-assignments', staff.id],
    queryFn: () => getTeacherAssignments({ teacher_id: staff.id }).then(unwrap),
  });

  const classes = arr<any>(classesQ.data);
  const subjects = arr<any>(subjectsQ.data);
  const assignments = arr<any>(assignmentsQ.data);

  const assign = async () => {
    if (!classId || !subjectId) return onError('Pick class and subject');
    if (staff.status !== 'active') return onError('Staff must be ACTIVE to receive assignments.');
    try {
      await postTeacherAssignment({ teacher_id: staff.id, school_class_id: Number(classId), subject_id: Number(subjectId) });
      setClassId(''); setSubjectId('');
      assignmentsQ.refetch();
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); }
  };

  const unassign = async (id: number) => {
    // Optimistic removal, no confirm prompt
    try {
      await deleteTeacherAssignment(id);
      assignmentsQ.refetch();
    } catch (e: any) { onError('Failed to unassign'); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Class</label>
          <select value={classId} onChange={e => setClassId(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
            <option value="">Select Class</option>{classes.map((c: any) => <option key={c.id} value={c.id}>{c.name} {c.arm ? `(${c.arm})` : ''}</option>)}
          </select>
        </div>
        <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Subject</label>
          <select value={subjectId} onChange={e => setSubjectId(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
            <option value="">Select Subject</option>{subjects.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>
      <button onClick={assign} className="w-full py-2.5 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800">Assign Subject</button>

      <div className="mt-8">
        <h4 className="font-bold text-slate-900 mb-3">Current Assignments ({assignments.length})</h4>
        {assignments.length === 0 ? <p className="text-sm text-slate-500 italic">No assignments yet.</p> : (
          <ul className="divide-y border border-slate-200 rounded-lg bg-white">
            {assignments.map((a: any) => (
              <li key={a.id} className="p-3 flex justify-between items-center text-sm">
                <span className="font-medium text-slate-800 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-slate-400" />
                  {a.class} {a.arm ? `(${a.arm})` : ''} - {a.subject}
                </span>
                <button onClick={() => unassign(a.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors" title="Unassign">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PayrollTab({ staff, onSave, onError }: any) {
  const [baseSalary, setBaseSalary] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().split('T')[0]);
  
  // Deductions state
  const [dedName, setDedName] = useState('');
  const [dedType, setDedType] = useState('fixed');
  const [dedValue, setDedValue] = useState('');

  const salariesQ = useQuery({ queryKey: ['payroll-salaries', staff.id], queryFn: () => getPayrollSalaries({ user_id: staff.id }).then(unwrap) });
  const salaryData = arr<any>(salariesQ.data).find((s: any) => s.user_id === staff.id);

  useEffect(() => {
    if (salaryData) {
      setBaseSalary(salaryData.base_salary ? String(salaryData.base_salary).replace(/[^0-9.]/g, '') : '');
      setEffectiveFrom(salaryData.effective_from ?? new Date().toISOString().split('T')[0]);
    }
  }, [salaryData]);

  const deductionsQ = useQuery({ queryKey: ['payroll-deductions'], queryFn: () => getPayrollDeductions().then(unwrap) });
  // Filter deductions for this specific staff (where user_id matches OR applies_to is 'all')
  const staffDeductions = arr<any>(deductionsQ.data).filter((d: any) => 
    d.user_id === staff.id || (d.user_id === null && d.applies_to === 'all')
  );

  const saveSalary = async () => {
    try {
      await putPayrollSalary(staff.id, { base_salary: Number(baseSalary), effective_from: effectiveFrom });
      onSave('Salary saved!');
      salariesQ.refetch();
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); }
  };

  const addDeduction = async () => {
    if (!dedName || !dedValue) return onError('Enter deduction name and value');
    try {
      await postPayrollDeduction({ name: dedName, type: dedType, value: Number(dedValue), user_id: staff.id, applies_to: 'teacher' });
      setDedName(''); setDedValue('');
      deductionsQ.refetch();
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); }
  };

  const removeDeduction = async (id: number) => {
    // Optimistic removal, no confirm prompt
    try {
      await deletePayrollDeduction(id);
      deductionsQ.refetch();
    } catch (e: any) { onError(e?.response?.data?.message ?? 'Failed'); }
  };

  return (
    <div className="space-y-8">
      {/* Salary Setup */}
      <div>
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" /> Base Salary</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Base Salary (₦)</label>
            <input type="number" value={baseSalary} onChange={e => setBaseSalary(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" placeholder="e.g. 150000" />
          </div>
          <div><label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Effective From</label>
            <input type="date" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
          </div>
        </div>
        <button onClick={saveSalary} className="mt-4 px-6 py-2.5 bg-[#0f172a] text-white rounded-lg font-bold text-sm shadow-sm hover:bg-slate-800">Save Salary</button>
      </div>

      <hr className="border-slate-200" />

      {/* Deductions Setup */}
      <div>
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2"><MinusCircle className="w-5 h-5 text-red-600" /> Staff Deductions</h3>
        
        <div className="grid grid-cols-3 gap-3 mb-4">
          <input value={dedName} onChange={e => setDedName(e.target.value)} placeholder="Deduction name" className="border border-slate-300 rounded-lg p-2.5 text-sm" />
          <select value={dedType} onChange={e => setDedType(e.target.value)} className="border border-slate-300 rounded-lg p-2.5 text-sm bg-white">
            <option value="fixed">Fixed (₦)</option><option value="percent">Percentage (%)</option>
            <option value="tax">Tax (%)</option><option value="pension">Pension (%)</option>
          </select>
          <div className="flex gap-2">
            <input type="number" value={dedValue} onChange={e => setDedValue(e.target.value)} placeholder="Value" className="flex-1 border border-slate-300 rounded-lg p-2.5 text-sm" />
            <button onClick={addDeduction} className="px-4 py-2.5 bg-slate-800 text-white rounded-lg font-bold text-sm">Add</button>
          </div>
        </div>

        {staffDeductions.length === 0 ? (
          <p className="text-sm text-slate-500 italic">No deductions configured for this staff.</p>
        ) : (
          <ul className="divide-y border border-slate-200 rounded-lg bg-white">
            {staffDeductions.map((d: any) => (
              <li key={d.id} className="p-3 flex justify-between items-center text-sm">
                <div>
                  <p className="font-medium text-slate-800">{d.name}</p>
                  <p className="text-xs text-slate-500 capitalize">{d.type} · {d.type === 'fixed' ? '₦' : ''}{d.value}{d.type !== 'fixed' ? '%' : ''}</p>
                </div>
                <button onClick={() => removeDeduction(d.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function PayrollHistoryTab({ staff }: { staff: any }) {
  const historyQ = useQuery({
    queryKey: ['payroll-history', staff.id],
    queryFn: () => getPayrollHistory({ user_id: staff.id }).then(unwrap),
  });
  const history = arr<any>(historyQ.data);

  if (historyQ.isLoading) return <p className="text-sm text-slate-500">Loading history...</p>;
  if (history.length === 0) return <p className="text-sm text-slate-500 italic">No payments recorded yet.</p>;

  return (
    <div className="space-y-4">
      <h3 className="font-bold text-slate-900 flex items-center gap-2"><DollarSign className="w-5 h-5 text-green-600" /> Payment History</h3>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left">Period</th>
              <th className="px-4 py-3 text-right">Amount</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {history.map((h: any) => (
              <tr key={h.id}>
                <td className="px-4 py-3 font-medium">{h.title || h.period}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-green-700">₦{Number(h.total_net || h.amount).toLocaleString()}</td>
                <td className="px-4 py-3"><span className="text-xs font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{h.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{h.paid_at ? new Date(h.paid_at).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/** Set a password for a staff member (works at once) or send them a temporary one by SMS. */
function StaffPasswordModal({ staff, onClose }: { staff: any; onClose: () => void }) {
  const [mode, setMode] = useState<'set' | 'temp'>('set');
  const [pw, setPw] = useState('');
  const [show, setShow] = useState(false);
  const [sms, setSms] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<{ msg: string; temp?: string } | null>(null);
  const name = staff.name ?? [staff.first_name, staff.last_name].filter(Boolean).join(' ');
  const go = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await api.post(`/school/staff/${staff.id}/reset-password`, mode === 'set' ? { password: pw, sms } : { sms: true });
      setDone({ msg: r.data?.message ?? 'Saved.', temp: r.data?.data?.temp_password });
    } catch (e: any) { setErr(e?.response?.data?.message ?? 'Could not change the password.'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-start">
          <div><h3 className="font-bold text-slate-900">Password for {name}</h3><p className="text-xs text-slate-500 mt-0.5">They sign in with {staff.phone ?? 'their phone'}{staff.email ? ` or ${staff.email}` : ''}.</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="p-5 space-y-3">
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3 flex gap-2"><CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />{done.msg}</p>
            {done.temp && <div className="text-center"><p className="text-xs text-slate-500">Temporary password</p><p className="font-mono text-2xl font-bold tracking-wider mt-1">{done.temp}</p></div>}
            <button onClick={onClose} className="w-full py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm">Done</button>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="inline-flex bg-slate-100 rounded-lg p-1 w-full">
              <button onClick={() => setMode('set')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${mode === 'set' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Set a password</button>
              <button onClick={() => setMode('temp')} className={`flex-1 px-3 py-1.5 rounded-md text-xs font-semibold ${mode === 'temp' ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}>Send a temporary one</button>
            </div>
            {mode === 'set' ? (
              <>
                <div className="relative">
                  <input type={show ? 'text' : 'password'} value={pw} onChange={(e) => setPw(e.target.value)} autoFocus autoComplete="new-password" placeholder="New password (at least 6 characters)" className="w-full border border-slate-300 rounded-lg p-2.5 pr-16 text-sm" />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 px-2">{show ? 'Hide' : 'Show'}</button>
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={sms} onChange={(e) => setSms(e.target.checked)} className="w-4 h-4" />Tell them by SMS that it changed (the password is not sent)</label>
                <p className="text-[11px] text-slate-500">It works straight away. They are signed out of every other device.</p>
              </>
            ) : <p className="text-sm text-slate-600">A temporary password is sent to {staff.phone ?? 'their phone'} by SMS and shown to you once. They must change it when they sign in.</p>}
            {err && <p className="text-sm text-red-700 bg-red-50 rounded-lg p-2.5 flex gap-2"><AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />{err}</p>}
            <div className="flex gap-2">
              <button onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button onClick={go} disabled={busy || (mode === 'set' && pw.length < 6)} className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">{busy ? 'Saving…' : mode === 'set' ? 'Save password' : 'Send temporary password'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
